/* eslint-disable camelcase */
import axios from 'axios';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { get } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { ButtonGroup, Col, Container, Form, InputGroup, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { updateUser } from '../../../../actions/auth.actions';
import Button from '../../../../shared/components/Button';
import '../../../../shared/components/Checkbox/Checkbox.scss';
import Loader from '../../../../shared/components/Loader/Loader';
import Select from '../../../../shared/components/Select';
import useIsMounted from '../../../../shared/hooks/useIsMounted';
import { GENDER_TYPES , toI18Code} from '../../../../utils/constants';
import i18n from '../../../../utils/i18n';

const validationSchema = Yup.object().shape({
  gender: Yup.string()
    .required(`${i18n.t('influencers.pages.profile.campaign.gender')} ${i18n.t('influencers.common.is_required')}`)
});

const CELL_PHONE_OS_TYPES = ['iOs', 'Android', 'Other'];

const CampaignProfile = () => {
  const user = useSelector(store => get(store, 'auth.user'));
  const loadingUser = useSelector(store => get(store, 'auth.loading'));
  const history = useHistory();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();

  useEffect(() => {
    if (!loadingUser && user.european) {
      history.replace('/profile');
    }
  }, [loadingUser]);

  const [state, setState] = useState({
    loading: false
  });

  useEffect(() => {
    setState({ ...state, loading: true });
    axios.get('/api/v1/countries')
      .then(({ data }) => {
        if (!isMounted) return;
        setState({
          ...state,
          loading: false,
          countries: data.countries || []
        });
      });
  }, []);

  const editableAttrs = () => {
    const initialValues = {};
    if (!user.editable_attributes) return initialValues;

    user.editable_attributes.map((attr) => {
      initialValues[attr.code] = user.object_attribute_values[attr.code];
    });
    return initialValues;
  };

  const formik = useFormik({
    initialValues: {
      gender: get(user, 'gender') || '',
      nationality: get(user, 'nationality') || '',
      cell_phone_os: get(user, 'cell_phone_os') || '',
      ...(editableAttrs())
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: values => {
      setState({ ...state, loading: true });
      axios.patch(`/api/v1/influencers/users/${user.id}`, {
        user: values
      }).then(({ data }) => {
        dispatch(updateUser(data.user));
        if (!isMounted) return;
        setState({ ...state, loading: false });
      });
    }
  });

  const handleDiscardChanges = useCallback(() => {
    document.querySelector('body').scrollTo({ top: 0, behavior: 'smooth' });
    formik.resetForm();
  }, [formik]);

  if (!user || !user.editable_attributes) return null;

  const formError = (element) => {
    if (get(formik, `touched.${element}}`, false) && get(formik, `errors.${element}`, false)) {
      return <Form.Text className="text-muted danger-text">
        {get(formik, `errors.${element}`, false)}
      </Form.Text>;
    }
  };

  const radioButtons = ({ i18_code, label, code, options }) =>
    <React.Fragment key={code}>
      <Row className="mt-4">
        <Col xs={12} sm={6}>
          <Row className="pl-3 mb-3">
            <h4>{ i18_code ? i18n.t(`influencers.pages.profile.campaign.questions.${i18_code}`) : label }</h4>
          </Row>
          <Row>
            {options.map((option) =>
              <Col xs={12} sm={6} key={option.id}>
                <InputGroup className="mb-3">
                  <input type="radio"
                    id={option.id}
                    name={code}
                    value={option.id}
                    checked={formik.values[code] == option.id}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur} />
                  <Form.Label htmlFor={option.id}>
                    { option.i18_code ? i18n.t(`influencers.pages.profile.campaign.questions_options.${option.i18_code}`) : option.label}
                  </Form.Label>
                </InputGroup>
              </Col>)}
          </Row>
          {formError(code)}
        </Col>
      </Row>
    </React.Fragment>;

  const Checkbox = ({ id, name, label, checked, ...props }) =>
    <div className="checkbox">
      <div className={classNames("checker", { checked })}>
        <input type="checkbox"
          name={name}
          id={id}
          checked={checked}
          {...props}
        />
        {checked && <i className={classNames("fal", "fa-check")}></i>}
      </div>
      {label && <label className='form-label' htmlFor={id}>{label}</label>}
    </div>;

  const checkBoxes = ({ i18_code, label, code, options }) =>
    <React.Fragment key={code}>
      <Row className="mt-4">
        <Col xs={12} sm={6}>
          <Row className="pl-3 mb-3">
            <h4>{ i18_code ? i18n.t(`influencers.pages.profile.campaign.questions.${i18_code}`) : label }</h4>
          </Row>
          <Row key={code}>
            {options.map((option) =>
              <Col xs={12} sm={6} key={option.id}>
                <InputGroup className="mb-3">
                  <Checkbox
                    id={option.id}
                    name={code}
                    value={option.id}
                    checked={formik.values[code] && formik.values[code].includes(`${option.id}`)}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label={
                      <span className="text-1">
                        { option.i18_code ? i18n.t(`influencers.pages.profile.campaign.questions_options.${option.i18_code}`) : option.label }
                      </span>
                    } />

                </InputGroup>
              </Col>
            )}
          </Row>
          {formError(code)}
        </Col>
      </Row>
    </React.Fragment>;

  const selectInput = (element, values) => <React.Fragment>
    <Row>
      <Col xs={12} sm={6} key={element}>
        <Form.Group>
          <Form.Label>{i18n.t(`influencers.pages.profile.campaign.${element}`)}</Form.Label>
          <Select
            className="mb-2"
            name={element}
            value={formik.values[element]}
            onChange={formik.handleChange}
            error={get(formik, `touched.${element}`, false) && get(formik, `errors.${element}`, false)}
            disabled={state.loading}
            options={values.map((value) => ({
              label: i18n.t(`influencers.pages.profile.campaign.${toI18Code(value)}`, { defaultValue: value }),
              value
            }))} />
        </Form.Group>
        {formError(element)}
      </Col>
    </Row>
  </React.Fragment>;

  return (
    <React.Fragment>
      {(state.loading || state.error) && <Loader loading={state.loading} error={state.error} />}

      {!state.loading && <Form onSubmit={formik.handleSubmit}>
        <Container className='page'>
          <h3 className='mb-4'>{i18n.t('influencers.pages.profile.campaign.title')}</h3>
          {selectInput('gender', GENDER_TYPES )}
          <hr className='my-4' />
          {!state.loading && user.editable_attributes && user.editable_attributes.map((attr) => {
            if (attr.is_multiple) {
              return checkBoxes(attr);
            }
            return radioButtons(attr);
          })}
          <hr className='my-4' />
          {state.countries && selectInput('nationality', state.countries.map(country => country.name))}
          <hr className='my-4' />
          {selectInput('cell_phone_os', CELL_PHONE_OS_TYPES)}
        </Container>
        <hr className='my-6' />
        <Container className='p-xs-0 mb-5'>
          <Row className='justify-content-end'>
            <div className='col-auto'>
              <ButtonGroup>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleDiscardChanges}
                  disabled={state.loading}>
                  {i18n.t('influencers.pages.profile.general.discard_changes')}
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={state.loading || !formik.isValid}>
                  {i18n.t('influencers.pages.profile.general.save_changes')}
                </Button>
              </ButtonGroup>
            </div>
          </Row>
        </Container>
      </Form>}
    </React.Fragment>
  );
};

export default CampaignProfile;
